/**
 * Verknüpfungen-Filter — URL-serialisable filter for the Forderungs-Fallübersicht
 * column "Verknüpfungen" (Issue #332).
 *
 * The filter distinguishes five mutually exclusive states:
 *   - empty            — no filtering
 *   - hasAll           — rows where G && M && W are all present
 *   - missing.G|M|W    — rows missing the respective link
 *   - demontage        — Werkstatt-Akte + Auslage (demontage_auslage=true)
 *
 * The URL key is `links` (e.g. `?links=GMW`, `?links=-W`, `?links=demontage`).
 * Unknown values are silently dropped so hand-crafted URLs stay harmless.
 */

export type VerknuepfungenKey = 'G' | 'M' | 'W';

export interface VerknuepfungenSet {
  G: boolean;
  M: boolean;
  W: boolean;
}

export interface VerknuepfungenFilter {
  hasAll?: true;
  missing?: Partial<Record<VerknuepfungenKey, true>>;
  demontage?: true;
}

/**
 * Row shape used by `matchesVerknuepfungenFilter`. Intentionally minimal so any
 * caller that can report G/M/W presence (and the demontage flag) can be tested.
 */
export interface VerknuepfungenRow extends VerknuepfungenSet {
  demontage_auslage?: boolean | null;
}

const LINKS_KEY = 'links';

export function parseVerknuepfungenFilter(search: string): VerknuepfungenFilter {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return {};
  const params = new URLSearchParams(raw);
  const value = params.get(LINKS_KEY);
  if (!value) return {};
  switch (value) {
    case 'GMW':
      return { hasAll: true };
    case '-G':
      return { missing: { G: true } };
    case '-M':
      return { missing: { M: true } };
    case '-W':
      return { missing: { W: true } };
    case 'demontage':
      return { demontage: true };
    default:
      return {};
  }
}

export function serializeVerknuepfungenFilter(f: VerknuepfungenFilter): string {
  if (f.hasAll) return `${LINKS_KEY}=GMW`;
  if (f.demontage) return `${LINKS_KEY}=demontage`;
  if (f.missing?.G) return `${LINKS_KEY}=-G`;
  if (f.missing?.M) return `${LINKS_KEY}=-M`;
  if (f.missing?.W) return `${LINKS_KEY}=-W`;
  return '';
}

export function matchesVerknuepfungenFilter(
  row: VerknuepfungenRow,
  filter: VerknuepfungenFilter,
): boolean {
  if (filter.hasAll) return row.G && row.M && row.W;
  if (filter.demontage) return row.W && row.demontage_auslage === true;
  if (filter.missing?.G) return !row.G;
  if (filter.missing?.M) return !row.M;
  if (filter.missing?.W) return !row.W;
  return true;
}
